import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { NgTemplateOutlet } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild, inject } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTooltipModule } from '@angular/material/tooltip';
import { filter, find, forEach, get, identity, isEmpty, map, max, set, some, sortBy, sumBy, take, toLower } from 'lodash-es';
import { Observable, filter as filterRx } from 'rxjs';
import { tap } from 'rxjs/operators';
import { CsvService } from '../../services';

export interface ImportColumn {
  headers: string[];
  label: string;
  field: string;
}

export interface ImportState {
  advanced: boolean;
  dirty: boolean;
  // Default columns definitions
  columns: Array<ImportColumn>;
  action: 'add' | 'edit' | 'full';
  /// Map column header to field name
  mapColumn?: (header: string) => string | undefined;
  validateRow: (row: Record<string, string>) => string | undefined;
  importRows: ( row: Array<Record<string, string>>, action: 'add' | 'edit' | 'full' ) => Observable<Array<string | undefined>>;
}

const SAMPLESIZE = 100;

@Component({
  selector: 'shared-import',
  templateUrl: './import.component.html',
  imports: [
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressBarModule,
    MatRadioModule,
    MatSelectModule,
    MatStepperModule,
    MatTooltipModule,
    NgTemplateOutlet,
    ReactiveFormsModule,
  ],
  styleUrls: ['./import.component.scss'],
})
export class ImportComponent implements OnInit {
  private formBuilder = inject(FormBuilder);
  private csvService = inject(CsvService);
  private dialog = inject(MatDialog);

  @Input()
    state!: ImportState;
  @Output() finished = new EventEmitter<boolean>();

  @ViewChild('addColumnDialog') addColumnDialog!: TemplateRef<unknown>;
  @ViewChild('helpDialog') helpDialog!: TemplateRef<unknown>;

  data!: FormGroup;

  header: string[] = [];
  rows: string[][] = [];
  sampleRows: string[][] = [];
  columns = new FormArray<FormControl>([]);
  availableColumns!: Array<ImportColumn>;
  usedColumns!: Array<ImportColumn>;
  importRows!: Array<{ error: string | undefined; data: Record<string, string>; }>;
  sampleImportRows!: Array<{ error: string | undefined; data: Record<string, string>; }>;
  displayedColumns: Array<string> = [];
  imported = false;
  hasError = false;
  done = false;
  errorCount = 0;
  deleteExisting = false;

  public _ = { get };

  get columnsAsFormGroup(): FormGroup {
    return this.columns as unknown as FormGroup;
  }

  ngOnInit(): void {
    this.data = this.formBuilder.group({
      header: true,
      action: 'add',
      data: ['', Validators.required],
    });

    this.data.valueChanges.subscribe(() => (this.state.dirty = true));

    this.columns = new FormArray<FormControl>([]);
    this.availableColumns = [
      { headers: [], label: '(Not used)', field: '' },
      ...this.state.columns,
    ];
  }

  loadFile(files: FileList | null) {
    if (!isEmpty(files)) {
      this.csvService.readTextFile(files![0]).subscribe((text) => {
        this.data.patchValue({ data: text });
        this.data.markAsDirty();
      });
    }
  }

  step(event: StepperSelectionEvent) {
    if (event.previouslySelectedIndex === 0 && this.data.dirty) {
      this.parse();
      this.data.markAsPristine();
    } else if (event.previouslySelectedIndex === 1 && this.columns.dirty) {
      this.createImportRows();
      this.columns.markAsPristine();
    } else if (event.selectedIndex === 3) {
      this.import();
    }
  }

  addColumn() {
    this.dialog.open(this.addColumnDialog, {
      data: { column: undefined, value: '' }
    }).afterClosed().pipe(
      filterRx(identity),
    ).subscribe((result) => {
      this.columns.push(new FormControl(result.column));
      this.header.push(result.column.headers[0]);
      forEach(this.rows, (row) => {
        row.push(result.value);
      });
      // Update the data to allow users to edit the file again
      this.data.patchValue({
        data: this.csvService.create(this.rows, map(this.columns, (column, index) => `${index}`), this.data.value.header ? this.header : undefined)
      });
    });
  }

  showHelp() {
    this.dialog.open(this.helpDialog, {
      data: this.state,
      width: '600px',
    });
  }

  private parse() {
    const result = this.csvService.parseCSV(this.data.value.data, this.data.value.header);
    this.rows = result.data;
    this.sampleRows = take(this.rows, SAMPLESIZE);
    this.header = result.header;
    this.createColumns();
  }

  private createColumns() {
    const mapColumn = this.state.mapColumn || ((header: string) => toLower(header).replace(/ +/g, ''));
    const count = max([0, ...map(this.rows, 'length')]);
    this.columns = new FormArray<FormControl>([]);
    for (let index = 0; index < count; index++) {
      const mapped = `${mapColumn(this.header[index])}`;
      const found = find(this.availableColumns, (column) => column.headers.includes(mapped));
      this.columns.push(new FormControl(found || this.availableColumns[0]));
    }
    this.columns.markAsDirty();
  }

  private createImportRows() {
    const columns = this.columns.value;
    this.usedColumns = filter(columns, (column) => column.field);
    this.importRows = map(this.rows, (row) => {
      const data = {};
      forEach(columns, (column, index) => {
        if (column.field) {
          set(data, column.field, get(row, index));
        }
      });
      return { error: this.state.validateRow(data), data };
    });

    this.sampleImportRows = take(sortBy(this.importRows, 'error'), SAMPLESIZE);
    this.hasError = some(this.importRows, (row) => !!row.error);
  }

  private import() {
    this.imported = true;

    const noErrors = filter(this.importRows, (row) => !row.error);

    if (isEmpty(noErrors)) {
      this.done = true;
      return;
    }
    this.state.importRows(map(noErrors, 'data'), this.state.action)
      .pipe(
        tap((results) => {
          forEach(results, (result, index) => {
            noErrors[index].error = result;
          });
        })
      ).subscribe({
        complete: () => {
          this.hasError = some(this.importRows, (row) => !!row.error);
          this.errorCount = sumBy(this.importRows, (row) => (row.error ? 1 : 0));
          this.sampleImportRows = take(sortBy(this.importRows, ['error']), SAMPLESIZE);
          this.state.dirty = false;
          this.done = true;
        },
      });
  }
}
